import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import { StudentAttendaceData } from "@src/pages/batches/attendance/attendance.types";

interface GetAllStudentResponse extends APIResponse {
  response: StudentAttendance[];
}

export interface StudentAttendance {
  id: number;
  name: string;
}

export type AttendanceMark = "PRESENT" | "ABSENT";

export type StudentAttendanceMap = Record<string, AttendanceMark>;

export interface BatchAttendanceRecord {
  id: string;
  collegeId: string;
  batchId: string;
  date: string;
  studentAttendance: StudentAttendanceMap;
  classSessionStatus: "INPROGRESS" | "COMPLETED";
  trainerAttendance?: "PRESENT" | "ABSENT" | null;
  startTime?: string;
  endTime?: string;
  name?: string;
}

interface GetBatchAttendanceRecord extends APIResponse {
  response: BatchAttendanceRecord[];
}

export interface TrainerAttendanceDTO {
  batchId: string;
  batchName: string;
  sessionId: string;
  sessionNumber: number;
  date: string;
  startTime: string;
  endTime: string;
  trainerId: string;
  trainerName: string;
  attendance: "PRESENT" | "ABSENT" | null;
}

export interface GetTrainerAttendanceResponse extends APIResponse {
  response: TrainerAttendanceDTO[];
}

export interface CreateAttendanceRequest {
  collegeId: string;
  batchId: string;
  date: string; // "YYYY-MM-DD"
  startTime: string; // ISO string or "HH:mm:ss"
  endTime: string; // looks like backend meant endTime (not endDate)
}

export interface MarkAttendanceRequest {
  sessionId: string;
  studentAttendance: StudentAttendanceMap;
}

export interface GetBatchesAttendanceReportResponse extends APIResponse {
  response: StudentAttendaceData[];
}

export const TPAttendanceApiSlice = createApi({
  reducerPath: "TPAttendanceApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getBatchStudents: builder.query<GetAllStudentResponse, { batchId: string }>(
      {
        query: ({ batchId }) => ({
          url: `/batch/get-student-data-by-batch`,
          params: { batchId: batchId },
        }),
      },
    ),
    getBatchAttendanceRecord: builder.query<
      GetBatchAttendanceRecord,
      { batchId: string; date: string }
    >({
      query: ({ batchId, date }) => ({
        url: `/attendance/get-attendance-by-date`,
        params: { batchId: batchId, date: date },
      }),
    }),
    getBatchAttendanceReport: builder.query<
      GetBatchesAttendanceReportResponse,
      { batchId: string; fromDate: string; toDate: string }
    >({
      query: ({ batchId, fromDate, toDate }) => ({
        url: `attendance/batches/${batchId}/students?fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
    }),
    getWeeklyRangeAttendance: builder.query<
      GetBatchAttendanceRecord,
      { batchId: string; startDate: string; endDate: string; sort?: boolean }
    >({
      query: ({ batchId, startDate, endDate, sort = false }) => {
        const params: {
          batchId: string;
          startDate: string;
          endDate: string;
          sort?: boolean;
        } = {
          batchId: batchId,
          startDate: startDate,
          endDate: endDate,
        };
        if (sort) params["sort"] = true;
        return {
          url: `/attendance/get-attendance-by-range`,
          params: params,
        };
      },
    }),
    getCollegeDailyAttendance: builder.query<
      any,
      { collegeId: string; fromDate: string; toDate: string }
    >({
      query: ({ collegeId, fromDate, toDate }) => ({
        url: `/attendance/${collegeId}/daily`,
        params: { fromDate, toDate },
        method: "GET",
      }),
    }),
    createAttendance: builder.mutation<APIResponse, CreateAttendanceRequest>({
      query: (body) => ({
        url: `/attendance`,
        method: "POST",
        body,
      }),
    }),
    endAttendanceSession: builder.mutation<APIResponse, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/attendance/${sessionId}/end`,
        method: "POST",
      }),
    }),
    markStudentAttendance: builder.mutation<APIResponse, MarkAttendanceRequest>(
      {
        query: (body) => ({
          url: `/attendance`,
          method: "POST",
          body,
        }),
      },
    ),
    updateTrainerAttendance: builder.mutation<
      APIResponse,
      { sessionId: string; status: "PRESENT" | "ABSENT" }
    >({
      query: ({ sessionId, status }) => ({
        url: `attendance/sessions/${sessionId}/trainer-attendance`,
        method: "PUT",
        params: { status },
      }),
    }),
    getTrainerAttendance: builder.query<
      GetTrainerAttendanceResponse,
      { batchId: string; fromDate?: string; toDate?: string }
    >({
      query: ({ batchId, fromDate, toDate }) => {
        const params: Record<string, string> = { batchId };
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        return {
          url: `attendance/trainer-attendance`,
          method: "GET",
          params,
        };
      },
    }),
    downloadAttendanceReport: builder.query<
      Blob,
      {
        batchId: string;
        fromDate?: string;
        toDate?: string;
        includeSessionWiseAttendance?: boolean;
      }
    >({
      query: ({ batchId, fromDate, toDate, includeSessionWiseAttendance }) => {
        const params: Record<string, string> = { batchId };
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (includeSessionWiseAttendance !== undefined) {
          params.includeSessionWiseAttendance = String(
            includeSessionWiseAttendance,
          );
        }
        return {
          url: `/attendance/download-report`,
          method: "GET",
          params,
          responseHandler: async (response: Response) => {
            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw (
                errorData || new Error("Failed to download attendance report")
              );
            }
            return await response.blob();
          },
        };
      },
    }),
  }),
});

export const {
  useGetBatchStudentsQuery,
  useLazyGetBatchStudentsQuery,
  useGetBatchAttendanceRecordQuery,
  useLazyGetBatchAttendanceRecordQuery,
  useGetBatchAttendanceReportQuery,
  useGetWeeklyRangeAttendanceQuery,
  useLazyGetWeeklyRangeAttendanceQuery,
  useGetCollegeDailyAttendanceQuery,
  useLazyGetCollegeDailyAttendanceQuery,
  useCreateAttendanceMutation,
  useEndAttendanceSessionMutation,
  useMarkStudentAttendanceMutation,
  useUpdateTrainerAttendanceMutation,
  useGetTrainerAttendanceQuery,
  useLazyGetTrainerAttendanceQuery,
  useLazyDownloadAttendanceReportQuery,
} = TPAttendanceApiSlice;
